import React from 'react';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { Result } from '@cp/shared/result';
import { ClientSubscriptionProcessingResult } from '@common/model/billing/clientSubscription';
import { useInjection } from '@cp/shared/di';
import { submitFormWithValues } from '@cp/utils/browser/submitForm';
import { clientSubscriptionApi } from 'src/entities/subscription';
import { useModal } from 'src/shared/modalProvider';
import { PAYMENT_SUCCESS_MODAL_NAME } from 'src/app/shared/components/modals/paymentSuccess/const';
import { LOGIN_FEATURE_TOKEN } from '../di';
import { useTariffModalProvider } from './useTariffModalProvider';
import { useCheckoutModalProvider } from './useCheckoutModalProvider';

/**
 * Subscription use-case entrypoint
 */
export const useStartBillingSubscription = () => {
  const login = useInjection(LOGIN_FEATURE_TOKEN);
  const isLoggedIn = useIsLoggedIn();
  const [loadSubscription] = clientSubscriptionApi.endpoints.getBillingSubscription.useLazyQuerySubscription();
  const [isLoading, setLoading] = React.useState(false);
  const selectTariff = useTariffModalProvider();
  const checkout = useCheckoutModalProvider();
  const { openModal } = useModal();

  return [
    React.useCallback(async (): Promise<Result<ClientSubscriptionProcessingResult>> => {
      setLoading(true);
      try {
        const { data } = await loadSubscription();
        const { tariffId } = data && data?.tariff && !data.tariff.deletedAt ? await selectTariff(data.tariff.id) : await selectTariff();
        if (!isLoggedIn) {
          await login();
          setLoading(false);
          return Result.Err(new Error('Login feature call'));
        }
        const result = await checkout(tariffId);
        if ('acs' in result) {
          const { url, ...values } = result.acs;
          submitFormWithValues(values, url, 'post');
          setLoading(false);
          return Result.Err(new Error('ACS redirect'));
        }
        // TODO await closing modal
        openModal(PAYMENT_SUCCESS_MODAL_NAME);
        return Result.Ok(result);
      } catch (err) {
        if (err instanceof Error && err.name === 'ModalError') {
          return Result.Err(err);
        }
        throw err;
      } finally {
        setLoading(false);
      }
    }, [openModal, isLoggedIn, login, selectTariff, loadSubscription, checkout]),
    { isLoading },
  ] as const;
};
